import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuthFetch } from '@/hooks/useAdmin';
import { ArrowLeft, Edit, Loader2, AlertTriangle, Eye, ExternalLink, Clock, User, Minus, Plus, Download, LayoutGrid, Columns3 } from 'lucide-react';
import { toast } from 'sonner';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function StatusPill({ s }) {
  const map = {
    published: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
    draft: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
    scheduled: 'bg-purple-500/20 text-purple-300 border-purple-500/40',
    archived: 'bg-zinc-700 text-zinc-300 border-zinc-600',
  };
  return (
    <span className={`px-2 py-0.5 text-[11px] uppercase tracking-wider rounded border ${map[s] || 'bg-zinc-700 text-zinc-300'}`}>{s}</span>
  );
}

function absUrl(u) {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  if (u.startsWith('/')) return `${BACKEND}${u}`;
  return u;
}

// WordPress saves thumbnails with `-NNNxNNN` suffix before the extension. Try
// to derive the original full-size URL so the lightbox opens at full quality.
function fullSizeVariant(u) {
  if (!u) return u;
  return u.replace(/-(\d{2,4})x(\d{2,4})(\.[a-z]+)(\?.*)?$/i, '$3$4');
}

function normalizeWpHtml(raw) {
  if (!raw) return '';
  let html = raw;
  html = html.replace(/<img\b([^>]*?)\s(?:width|height)="[^"]*"/gi, '<img$1');
  html = html.replace(/<img\b([^>]*?)\sstyle="[^"]*"/gi, '<img$1');
  html = html.replace(
    /<(ul|figure)\s+class="([^"]*wp-block-gallery[^"]*)"/gi,
    '<$1 data-wp-gallery="1" class="$2"',
  );
  return html;
}

export default function ArticlePreview() {
  const { id } = useParams();
  const authFetch = useAuthFetch();
  const [article, setArticle] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [columns, setColumns] = useState(3);
  const [saving, setSaving] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [galleryLayout, setGalleryLayout] = useState('grid'); // 'grid' | 'masonry'
  const [refetching, setRefetching] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await authFetch(`${API}/admin/articles/${id}/preview`);
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || 'Greška pri dohvatu članka');
        if (!mounted) return;
        setArticle(d.article);
        setCategory(d.category);
        const saved = Number(d.article.gallery_columns);
        setColumns(Number.isFinite(saved) && saved >= 1 && saved <= 6 ? saved : 3);
      } catch (e) {
        if (mounted) setErr(e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id, authFetch]);

  const normalizedBody = useMemo(() => {
    if (!article) return '';
    return normalizeWpHtml(article.content_hr || article.body_hr || '');
  }, [article]);

  const gallery = useMemo(() => {
    if (!article) return [];
    return (article.gallery_image_urls || []).map(absUrl).filter(Boolean);
  }, [article]);

  async function saveColumns(next) {
    if (!article) return;
    setColumns(next);
    setSaving(true);
    try {
      await authFetch(`${API}/admin/articles/${article.id}`, {
        method: 'PUT',
        body: JSON.stringify({ gallery_columns: next }),
      });
    } catch (e) {
      // non-fatal: keep the state change locally
    } finally {
      setSaving(false);
    }
  }

  async function refetchImages() {
    if (!article || refetching) return;
    setRefetching(true);
    try {
      const r = await authFetch(`${API}/admin/articles/${article.id}/refetch-images`, {
        method: 'POST',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Ponovno dohvaćanje nije uspjelo');
      const parts = [];
      if (d.downloaded_inline) parts.push(`${d.downloaded_inline} u tekstu`);
      if (d.gallery_added) parts.push(`${d.gallery_added} u galeriji`);
      if (d.cover_downloaded) parts.push('naslovna slika');
      if (parts.length === 0) {
        toast.success('Sve slike su već lokalne — nema novih za preuzeti.');
      } else {
        toast.success(`Preuzeto: ${parts.join(', ')}.`);
      }
      // Re-load the article to pick up new gallery + body
      const re = await authFetch(`${API}/admin/articles/${article.id}/preview`);
      const rd = await re.json();
      if (re.ok) {
        setArticle(rd.article);
        setCategory(rd.category);
      }
    } catch (e) {
      toast.error(e.message || 'Greška pri preuzimanju slika.');
    } finally {
      setRefetching(false);
    }
  }

  const [reimporting, setReimporting] = useState(false);
  async function reimportFromSource() {
    if (!article || reimporting) return;
    if (!window.confirm('Ponovno preuzmi članak iz izvornog URL-a? Ovo će prepisati naslov, sažetak, tekst, naslovnu sliku i galeriju.')) return;
    setReimporting(true);
    try {
      const r = await authFetch(`${API}/admin/articles/${article.id}/reimport-from-source`, {
        method: 'POST',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Ponovni uvoz nije uspio');
      toast.success(`Članak ponovno uvezen (${d.body_chars} znakova teksta, ${d.gallery_count} slika u galeriji).`);
      const re = await authFetch(`${API}/admin/articles/${article.id}/preview`);
      const rd = await re.json();
      if (re.ok) { setArticle(rd.article); setCategory(rd.category); }
    } catch (e) {
      toast.error(e.message || 'Greška pri ponovnom uvozu.');
    } finally {
      setReimporting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-zinc-500" data-testid="preview-loading">
        <Loader2 className="animate-spin mr-2" size={18} /> Učitavanje pregleda…
      </div>
    );
  }
  if (err || !article) {
    return (
      <div className="p-6 max-w-3xl mx-auto" data-testid="preview-error">
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 text-red-200 p-4 flex items-start gap-3">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">Pregled nije dostupan</div>
            <div className="text-sm opacity-90 mt-1">{err || 'Članak nije pronađen.'}</div>
          </div>
        </div>
      </div>
    );
  }

  const publishDate = article.publish_at ? new Date(article.publish_at).toLocaleString('hr') : '—';
  const sourceName = article.source_name || article.source_credit || '';
  const coverUrl = absUrl(article.cover_image_url);
  const clampedCols = Math.min(Math.max(columns, 1), 6);
  const lightboxSlides = gallery.map((u) => {
    const full = fullSizeVariant(u);
    return {
      src: absUrl(full || u),
      thumb: absUrl(u),
    };
  });

  return (
    <div className="max-w-4xl mx-auto p-6 lg:p-8" data-testid="article-preview">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-6 gap-3 sticky top-0 z-10 bg-zinc-950/85 backdrop-blur py-3 -mx-6 px-6 border-b border-zinc-800">
        <Link to="/admin/articles" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-200" data-testid="back-to-list">
          <ArrowLeft size={16} /> Natrag na članke
        </Link>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refetchImages}
            disabled={refetching}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-zinc-700 text-zinc-200 hover:bg-zinc-800 disabled:opacity-50"
            title="Ponovno skeniraj članak i preuzmi slike koje su bile preskočene"
            data-testid="refetch-images-btn"
          >
            {refetching ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {refetching ? 'Preuzimam…' : 'Preuzmi još slike'}
          </button>
          {(article.source_article_url || article.source_url) && (
            <button
              type="button"
              onClick={reimportFromSource}
              disabled={reimporting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-amber-600/50 text-amber-300 hover:bg-amber-600/10 disabled:opacity-50"
              title="Ponovno preuzmi sav sadržaj članka iz originalnog izvora i prepiši lokalnu verziju"
              data-testid="reimport-from-source-btn"
            >
              {reimporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {reimporting ? 'Uvozim…' : 'Ponovno uvezi iz izvora'}
            </button>
          )}
          {article.status === 'published' && (
            <a
              href={`/clanak/${article.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
              data-testid="open-live"
            >
              <Eye size={14} /> Otvori živo
            </a>
          )}
          <Link
            to={`/admin/articles/${article.id}/edit`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="edit-from-preview"
          >
            <Edit size={14} /> Uredi
          </Link>
        </div>
      </div>

      {/* Header */}
      <div className="mb-6">
        {category?.name_hr && (
          <span
            className="inline-block text-[11px] font-bold uppercase tracking-wider text-white px-2 py-0.5 rounded mb-3"
            style={{ backgroundColor: category.color || '#3b82f6' }}
          >
            {category.name_hr}
          </span>
        )}
        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-zinc-100 leading-tight mb-3" data-testid="preview-title">
          {article.title_hr}
        </h1>
        <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-400">
          <StatusPill s={article.status} />
          {article.author_name && <span className="inline-flex items-center gap-1.5"><User size={14} /> {article.author_name}</span>}
          <span className="inline-flex items-center gap-1.5"><Clock size={14} /> {publishDate}</span>
        </div>
        {article.excerpt_hr && (
          <p
            className="mt-4 text-zinc-300 text-lg leading-relaxed border-l-2 border-red-500 pl-4 line-clamp-4"
            title={article.excerpt_hr}
            data-testid="preview-excerpt"
          >
            {article.excerpt_hr}
          </p>
        )}
      </div>

      {coverUrl && (
        <div className="aspect-[16/9] rounded-xl overflow-hidden mb-8 bg-zinc-900">
          <img src={coverUrl} alt={article.title_hr} className="w-full h-full object-cover" data-testid="preview-cover" />
        </div>
      )}

      {normalizedBody ? (
        <>
          <article
            className="wp-article-preview prose prose-lg prose-invert prose-zinc max-w-none
                       prose-headings:font-serif prose-headings:text-zinc-100
                       prose-p:text-zinc-300 prose-p:leading-relaxed
                       prose-a:text-red-400 prose-strong:text-zinc-100
                       prose-li:text-zinc-300 prose-blockquote:border-l-red-500 prose-blockquote:text-zinc-200
                       prose-img:rounded-lg prose-img:mx-auto prose-img:my-4
                       prose-figure:my-4"
            dangerouslySetInnerHTML={{ __html: normalizedBody }}
            data-testid="preview-body"
          />
          <style>{`
            .wp-article-preview img {
              max-width: 100% !important; width: auto !important; height: auto !important; display: block;
            }
            .wp-article-preview figure { margin: 1rem 0; }
            .wp-article-preview figcaption { text-align: center; color: #a1a1aa; font-size: 0.85rem; margin-top: 0.5rem; }
            .wp-article-preview [data-wp-gallery="1"] {
              display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
              gap: 0.5rem; padding: 0; margin: 1.5rem 0; list-style: none;
            }
            .wp-article-preview [data-wp-gallery="1"] > li,
            .wp-article-preview [data-wp-gallery="1"] > figure { margin: 0; }
            .wp-article-preview [data-wp-gallery="1"] img {
              aspect-ratio: 1 / 1; object-fit: cover; border-radius: 0.5rem; width: 100%; max-width: 100%;
            }
            .wp-article-preview .wp-block-image { margin: 1rem 0; }
            .wp-article-preview iframe { max-width: 100%; }
          `}</style>
        </>
      ) : (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-200" data-testid="preview-empty-body">
          Članak nema sadržaja. Ponovite uvoz s uključenom opcijom &quot;WP REST API fallback&quot;.
        </div>
      )}

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="mt-10 border-t border-zinc-800 pt-6" data-testid="preview-gallery">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h3 className="font-serif text-xl text-zinc-100">
              Galerija <span className="text-sm text-zinc-500 font-sans ml-2">({gallery.length} {gallery.length === 1 ? 'fotografija' : gallery.length < 5 ? 'fotografije' : 'fotografija'})</span>
            </h3>
            <div className="flex items-center gap-3">
              <div className="inline-flex rounded-md border border-zinc-700 overflow-hidden" data-testid="gallery-layout-toggle">
                <button
                  type="button"
                  onClick={() => setGalleryLayout('grid')}
                  className={`px-2.5 py-1.5 text-xs inline-flex items-center gap-1 ${galleryLayout === 'grid' ? 'bg-zinc-800 text-zinc-100' : 'text-zinc-400 hover:bg-zinc-900'}`}
                  data-testid="gallery-layout-grid"
                  title="Kvadratna mreža — jednake plohe"
                >
                  <LayoutGrid size={12} /> Mreža
                </button>
                <button
                  type="button"
                  onClick={() => setGalleryLayout('masonry')}
                  className={`px-2.5 py-1.5 text-xs inline-flex items-center gap-1 border-l border-zinc-700 ${galleryLayout === 'masonry' ? 'bg-zinc-800 text-zinc-100' : 'text-zinc-400 hover:bg-zinc-900'}`}
                  data-testid="gallery-layout-masonry"
                  title="Masonry — portretne i pejzažne slike miješane"
                >
                  <Columns3 size={12} /> Masonry
                </button>
              </div>
              <div className="flex items-center gap-2 text-xs text-zinc-400" data-testid="column-control">
                <span>Kolona:</span>
                <button
                  type="button"
                  disabled={clampedCols <= 1 || saving}
                  onClick={() => saveColumns(clampedCols - 1)}
                  className="w-7 h-7 rounded border border-zinc-700 hover:bg-zinc-800 disabled:opacity-30 inline-flex items-center justify-center"
                  data-testid="gallery-cols-dec"
                  aria-label="Manje kolona"
                >
                  <Minus size={12} />
                </button>
                <span className="font-semibold text-zinc-200 w-5 text-center" data-testid="gallery-cols-value">{clampedCols}</span>
                <button
                  type="button"
                  disabled={clampedCols >= 6 || saving}
                  onClick={() => saveColumns(clampedCols + 1)}
                  className="w-7 h-7 rounded border border-zinc-700 hover:bg-zinc-800 disabled:opacity-30 inline-flex items-center justify-center"
                  data-testid="gallery-cols-inc"
                  aria-label="Više kolona"
                >
                  <Plus size={12} />
                </button>
              </div>
            </div>
          </div>
          {galleryLayout === 'grid' ? (
            <div
              className="grid gap-2"
              style={{ gridTemplateColumns: `repeat(${clampedCols}, minmax(0, 1fr))` }}
              data-testid="gallery-grid"
            >
              {gallery.map((url, i) => (
                <button
                  key={`${url}-${i}`}
                  type="button"
                  onClick={() => setLightboxIndex(i)}
                  className="group relative aspect-square overflow-hidden rounded-md bg-zinc-900 ring-1 ring-zinc-800 hover:ring-red-400 transition focus:outline-none focus:ring-2 focus:ring-red-400"
                  data-testid={`gallery-thumb-${i}`}
                  aria-label={`Fotografija ${i + 1}`}
                >
                  <img
                    src={url}
                    alt={`Gallery ${i + 1}`}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                  <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition" />
                </button>
              ))}
            </div>
          ) : (
            <div
              className="gap-2 [column-fill:_balance]"
              style={{ columnCount: clampedCols, columnGap: '0.5rem' }}
              data-testid="gallery-masonry"
            >
              {gallery.map((url, i) => (
                <button
                  key={`${url}-${i}-m`}
                  type="button"
                  onClick={() => setLightboxIndex(i)}
                  className="group block w-full mb-2 break-inside-avoid overflow-hidden rounded-md bg-zinc-900 ring-1 ring-zinc-800 hover:ring-red-400 transition focus:outline-none focus:ring-2 focus:ring-red-400"
                  style={{ breakInside: 'avoid' }}
                  data-testid={`gallery-masonry-thumb-${i}`}
                  aria-label={`Fotografija ${i + 1}`}
                >
                  <img
                    src={url}
                    alt={`Gallery ${i + 1}`}
                    className="w-full h-auto block transition-transform duration-300 group-hover:scale-[1.02]"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          )}

          <Lightbox
            open={lightboxIndex >= 0}
            close={() => setLightboxIndex(-1)}
            index={lightboxIndex < 0 ? 0 : lightboxIndex}
            slides={lightboxSlides}
            carousel={{ finite: false }}
            controller={{ closeOnBackdropClick: true }}
            on={{ view: ({ index }) => setLightboxIndex(index) }}
            render={{
              slide: ({ slide }) => (
                <img
                  src={slide.src}
                  alt=""
                  onError={(e) => { if (slide.thumb && e.currentTarget.src !== slide.thumb) e.currentTarget.src = slide.thumb; }}
                  style={{ maxWidth: '100%', maxHeight: '100vh', objectFit: 'contain', margin: '0 auto', display: 'block' }}
                />
              ),
            }}
          />
        </section>
      )}

      {/* Source footer */}
      {(sourceName || article.source_url) && (
        <div
          className="mt-10 text-xs text-zinc-400 bg-zinc-900/60 border border-zinc-800 rounded-lg px-4 py-3 flex items-center gap-2"
          data-testid="preview-source"
        >
          <ExternalLink size={12} />
          <span>
            Izvor:{' '}
            {article.source_url ? (
              <a
                href={article.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-red-400 hover:underline"
                data-testid="preview-source-link"
              >
                {sourceName || article.source_url}
              </a>
            ) : (
              <strong className="text-zinc-200">{sourceName}</strong>
            )}
          </span>
        </div>
      )}
    </div>
  );
}
